import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FiChevronLeftImg from '../../assets/images/fi_chevron_left.png';
import FiChevronRightImg from '../../assets/images/fi_chevron_right.png';
import FiSearch from '../../assets/images/fi_search.png';
import BackArrow from '../../assets/images/Frame_18177.png';
import axiosInstance from '../../api/axiosInstance';
import axios from 'axios';
import StepOne from './steps/StepOne';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import InactivityTimer from '../../components/InactivityTimer';

const BookConsultation = () => {

    const location = useLocation();
    const navigate = useNavigate();
    
    // Get patient ID from Redux token
    const token = useSelector((state) => state.auth.kiosk_token);

    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [selectedSlotData, setSelectedSlotData] = useState(null); // Store full slot object
    const [filteredDoctors, setFilteredDoctors] = useState([]);
    const [isDoctorSelected, setIsDoctorSelected] = useState(false);

    // API states
    const [allDepartments, setAllDepartments] = useState([]);
    const [filteredDepartments, setFilteredDepartments] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [departmentDoctors, setDepartmentDoctors] = useState({});
    const [loading, setLoading] = useState(true);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [morningSlots, setMorningSlots] = useState([]);
    const [afternoonSlots, setAfternoonSlots] = useState([]);
    const [eveningSlots, setEveningSlots] = useState([]);
    const [formData, setFormData] = useState({
        patientType: '',
    });
    const [errors, setErrors] = useState({});

    // Validate token exists and redirect if not
    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
        }
    }, [token, navigate]);

    useEffect(() => {
        getAllDepartments();
    }, []);

    // Filter departments based on search query
    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredDepartments(allDepartments);
        } else {
            const filtered = allDepartments.filter(dept => 
                (dept.departmentName || dept.deptName || '')
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
            );
            setFilteredDepartments(filtered);
        }
    }, [searchQuery, allDepartments]);

    // Return early if no token to prevent errors
    if (!token) {
        return null;
    }

    // Decode token to get patient ID
    const decodedToken = jwtDecode(token);
    const patientId = decodedToken.id;

    // Fetch available time slots for selected doctor
    const fetchAvailableSlots = async (deptId, employeeId) => {
        try {
            setSlotsLoading(true);
            const today = new Date();
            const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
            
            const response = await axios.get(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/kiosk/availableDoctorSlots?deptId=${deptId}&employeeId=${employeeId}&date=${dateStr}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                    },
                }
            );

            console.log('Available slots response:', response.data);

            if (response.data && (response.data.status === 'success' || response.data.status === 'Success') && response.data.data) {
                const slots = response.data.data;
                setAvailableSlots(slots);
                
                // Categorize slots into morning, afternoon, evening
                categorizeSlots(slots);
            } else {
                setAvailableSlots([]);
                setMorningSlots([]);
                setAfternoonSlots([]);
                setEveningSlots([]);
            }
            setSlotsLoading(false);
        } catch (error) {
            console.error('Error fetching available slots:', error);
            setSlotsLoading(false);
            setAvailableSlots([]);
            setMorningSlots([]);
            setAfternoonSlots([]);
            setEveningSlots([]);
        }
    };

    // Categorize slots based on time
    const categorizeSlots = (slots) => {
        const morning = [];
        const afternoon = [];
        const evening = [];

        slots.forEach(slot => {
            // Extract hour from slotTimeStr (format like "13:30 PM" or "14:00 PM")
            const timeStr = slot.slotTimeStr || '';
            const hourMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
            
            if (hourMatch) {
                const hour = parseInt(hourMatch[1], 10);
                
                // Categorize by 24-hour format (slotTimeStr appears to use 24-hour format despite PM)
                if (hour >= 6 && hour < 12) {
                    morning.push(slot);
                } else if (hour >= 12 && hour < 17) {
                    afternoon.push(slot);
                } else {
                    evening.push(slot);
                }
            }
        });

        setMorningSlots(morning);
        setAfternoonSlots(afternoon);
        setEveningSlots(evening);
    };

    const slots = [
        { time: "09:00 - 09:15", status: "Available" },
        { time: "09:15 - 09:30", status: "Available" },
        { time: "09:30 - 09:45", status: "Available" },
        { time: "09:45 - 10:00", status: "Available" },
        { time: "10:00 - 10:15", status: "Available" },
        { time: "10:15 - 10:30", status: "Booked" },
        { time: "10:30 - 10:45", status: "Booked" },
        { time: "10:45 - 11:00", status: "Available" },
        { time: "11:00 - 11:15", status: "Available" },
        { time: "11:15 - 11:30", status: "Booked" },
        { time: "11:30 - 11:45", status: "Available" },
        { time: "11:45 - 12:00", status: "Booked" },
        { time: "12:00 - 12:15", status: "Available" },
    ];

    // Fetch all departments and their doctors
    const getAllDepartments = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/opd/deptList?branchId=${localStorage.getItem('site_id')}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });
            const departments = response.data.data;
            setAllDepartments(departments);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching departments:', error);
            setLoading(false);
        }
    };

    const getDepartmentDoctors = async (departmentId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/opd/deptDocList?deptId=${departmentId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching doctors for department:', error);
            return [];
        }
    };

    // Decode token to get patient ID (already declared at line 76)
    // const decodedToken = jwtDecode(token); // REMOVED DUPLICATE
    // const patientId = decodedToken.id; // Already declared at line 77

    const handleDepartmentChange = async (department) => {
        setSelectedDepartment(department);
        setSelectedDoctor(null); // Reset selected doctor when department changes
        setIsDoctorSelected(false); // Hide slots until doctor is selected
        setFilteredDoctors([]); // Clear previous doctors
        setSelectedSlot(''); // Clear selected slot
        
        setErrors((prevErrors) => ({
            ...prevErrors,
            department: ''  // Clear department error when selected
        }));

        // Fetch doctors for the selected department
        try {
            const doctors = await getDepartmentDoctors(department.departmentId);
            setFilteredDoctors(doctors);
            
            // Store in departmentDoctors cache for future use
            setDepartmentDoctors(prev => ({
                ...prev,
                [department.departmentId]: doctors
            }));
        } catch (error) {
            console.error('Error loading doctors:', error);
            setFilteredDoctors([]);
        }
    };

    const handleDoctorChange = (doctor) => {
        setSelectedDoctor(doctor);
        setIsDoctorSelected(true); // Show slots after selecting doctor
        setSelectedSlot(''); // Reset slot when doctor changes

        setErrors((prevErrors) => ({
            ...prevErrors,
            doctor: ''  // Clear doctor error when selected
        }));

        // Fetch available slots for the selected doctor
        if (selectedDepartment && doctor) {
            fetchAvailableSlots(
                selectedDepartment.departmentId,
                doctor.empId || doctor.consultantId
            );
        }
    };

    const handleSlotChange = (e) => {
        const slotTimeStr = e.target.value;
        setSelectedSlot(slotTimeStr);
        
        // Find the full slot object from availableSlots
        const slotObject = availableSlots.find(slot => slot.slotTimeStr === slotTimeStr);
        setSelectedSlotData(slotObject);

        setErrors((prevErrors) => ({
            ...prevErrors,
            slot: ''  // Clear slot error when selected
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // Today's date only
        const today = new Date();

        try {
            // Prepare visit data
            const visitData = {
                patientId: patientId,
                departmentId: selectedDepartment.departmentId,
                departmentName: selectedDepartment.departmentName || selectedDepartment.deptName,
                doctorId: selectedDoctor.empId || selectedDoctor.consultantId,
                doctorName: selectedDoctor.empName || selectedDoctor.consultantName,
                empNo: selectedDoctor.empNo,
                serviceCode: selectedDoctor.serviceCode,
                serviceId: selectedDoctor.serviceId,
                serviceName: selectedDoctor.serviceName,
                hospitalId: selectedDoctor.hospitalId,
                consultationFee: selectedDoctor.totalAmount,
                visitDate: formatDate(today),
                visitTime: selectedSlot,
                weekDay: selectedDoctor.weekDay,
                dayName: selectedDoctor.dayName,
                unitId: selectedSlotData?.unit?.toString() || null // Add unitId from selected slot
            };

            console.log('Creating consultation order with data:', visitData);

            // Create consultation order
            const response = await axiosInstance.post('/api/opd-visit/create-order', {
                ...visitData,
                siteCode: localStorage.getItem('site_code') || null,
                himsToken: localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1') || null, // HIMS token for server-side fee verification
            });

            if (response.data.success) {
                const orderData = response.data.data;

                // Check if it's a free consultation (zero fee)
                if (orderData.freeConsultation) {
                    // Skip payment gateway and redirect directly to success page
                    navigate(`/opd-visit-success?visitNo=${orderData.visitNo}`);
                    return;
                }

                // For non-zero fees, proceed with Razorpay payment
                // Create form and submit to Razorpay hosted checkout
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = 'https://api.razorpay.com/v1/checkout/embedded';

                const formFields = {
                    key_id: orderData.keyId,
                    amount: (orderData.amount * 100).toString(), // Convert to paise
                    currency: orderData.currency,
                    name: "KIMS Hospital",
                    description: `OPD Consultation - ${orderData.doctorName}`,
                    image: "/logo.jpg",
                    order_id: orderData.orderId,
                    callback_url: `${axiosInstance.defaults.baseURL}/api/opd-visit/payment-callback`,
                    cancel_url: `${axiosInstance.defaults.baseURL}/api/patient/cancel-payment`,
                    notes: {
                        visitNo: orderData.visitNo,
                        doctorName: orderData.doctorName,
                        departmentName: orderData.departmentName,
                        visitDate: orderData.visitDate,
                        visitTime: orderData.visitTime,
                        payment_type: 'opd_consultation'
                    },
                    prefill: {
                        name: orderData.patientName || '',
                        email: orderData.patientEmail || '',
                        contact: orderData.patientContact || '',
                    },
                    theme: { color: "#0FA968" },
                };

                // Add all form fields
                Object.keys(formFields).forEach((key) => {
                    if (typeof formFields[key] === 'object') {
                        Object.keys(formFields[key]).forEach((subKey) => {
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = `${key}[${subKey}]`;
                            input.value = formFields[key][subKey];
                            form.appendChild(input);
                        });
                    } else {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = formFields[key];
                        form.appendChild(input);
                    }
                });

                document.body.appendChild(form);
                form.submit();
            } else {
                alert('Failed to create consultation order. Please try again.');
            }
        } catch (error) {
            console.error('Error creating consultation order:', error);
            alert('Error processing request. Please try again.');
        }
    }

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
    }

    // Always today's date
    const today = new Date();

    // Function to format date in dd-mm-yyyy
    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const validateForm = () => {
        const formErrors = {};
        if (!selectedDepartment) formErrors.department = 'Department is required';
        if (!selectedDoctor) formErrors.doctor = 'Doctor is required';
        if (!selectedSlot) formErrors.slot = 'Slot is required';
        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    return (
        <>
            <InactivityTimer />
            <div className="page-container flex flex-col">
                <div className="form-container flex-grow overflow-auto">
                    <StepOne />
                    <div className='px-4 sm:px-8 lg:px-14 mt-6'>
                        <form onSubmit={handleSubmit}>

                            <div className="flex flex-col space-y-2 mt-6">
                                <label className="text-base sm:text-lg font-semibold">
                                    Consultation Date <span className="text-red-500">*</span>
                                </label>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                    <div className="flex items-center">
                                        <div className="text-sm sm:text-[16px] font-semibold bg-[#0FA968] text-white px-4 py-2 rounded whitespace-nowrap">
                                            Today - {formatDate(today)}
                                        </div>
                                    </div>

                                    <div className="relative flex w-full sm:w-auto">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3.5">
                                            <img src={FiSearch} alt='search' className="w-5 h-5 text-gray-500" />
                                        </div>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={handleInputChange}
                                            className={`bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full sm:w-[320px] lg:w-[400px] pl-10 p-2 focus:outline-none focus:ring-2 ring-blue-500 focus:ring-gray-600 focus:border-transparent h-10`}
                                            placeholder="Search doctor/department/symptom, etc..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Department Selection */}
                            {loading ? (
                                <div className='mt-6 text-center'>
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA968]"></div>
                                    <p className="mt-2 text-gray-600">Loading departments...</p>
                                </div>
                            ) : (
                                <div className='mt-6'>
                                    <div className='flex items-center space-x-2 sm:space-x-4'>
                                        <button
                                            type="button"
                                            className="hidden sm:block px-3 py-2 bg-white rounded hover:bg-gray-400 shrink-0"
                                            onClick={() => {
                                                const container = document.getElementById('department-scroll-container');
                                                container.scrollBy({ left: -300, behavior: 'smooth' });
                                            }}
                                        >
                                            <img src={FiChevronLeftImg} alt='left' className="w-5 h-5" />
                                        </button>

                                        <div id="department-scroll-container" className='overflow-x-auto hide-scrollbar-department flex-grow'>
                                            <div className='flex space-x-5 max-content-width'>
                                                {filteredDepartments.map((department) => (
                                                    <label key={department.departmentId || department.deptId} className='flex items-center cursor-pointer'>
                                                        <input
                                                            type='radio'
                                                            name='department'
                                                            value={department.departmentId}
                                                            checked={selectedDepartment?.departmentId === department.departmentId}
                                                            onChange={() => handleDepartmentChange(department)}
                                                            className='hidden'
                                                        />
                                                        <span
                                                            className={`px-3 py-2 bg-white rounded-2xl text-[#27272A] whitespace-nowrap ${selectedDepartment?.departmentId === department.departmentId
                                                                ? 'bg-[#DAFEEF] text-[#0FA968] border border-[#0FA968]'
                                                                : 'hover:bg-[#DAFEEF] hover:text-[#0FA968] hover:border hover:border-1 hover:border-[#0FA968]'
                                                                }`}
                                                        >
                                                            {department.departmentName || department.deptName}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="hidden sm:block px-3 py-2 bg-white rounded hover:bg-gray-400 shrink-0"
                                            onClick={() => {
                                                const container = document.getElementById('department-scroll-container');
                                                container.scrollBy({ left: 300, behavior: 'smooth' });
                                            }}
                                        >
                                            <img src={FiChevronRightImg} alt='right' className="w-5 h-5" />
                                        </button>
                                    </div>
                                    {errors.department && <p className="text-red-500 text-sm mt-1">{errors.department}</p>}
                                </div>
                            )}

                            {selectedDepartment && filteredDoctors.length > 0 && (
                                <div className='mt-6'>
                                    <p className='text-[#0FA968] text-[16px] font-roboto font-semibold mb-3'>SELECT DOCTOR</p>
                                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'>
                                        {filteredDoctors.map((doctor, index) => (
                                            <label 
                                                key={doctor.empId || doctor.consultantId || doctor.id || index} 
                                                className='flex flex-col cursor-pointer'
                                                onClick={() => handleDoctorChange(doctor)}
                                            >
                                                <span
                                                    className={`px-3 py-2 bg-white rounded-lg text-[#27272A] mb-2 ${(selectedDoctor?.empId === doctor.empId)
                                                        ? 'bg-[#DAFEEF] text-[#0FA968] border-2 border-[#0FA968]'
                                                        : 'hover:bg-[#DAFEEF] hover:text-[#0FA968] hover:border hover:border-1 hover:border-[#0FA968] border border-gray-200'
                                                        }`}
                                                >
                                                    {doctor.empName || doctor.consultantName || doctor.name}
                                                    {(doctor.designation || doctor.serviceName) && (
                                                        <p className='text-sm text-gray-600'>
                                                            {doctor.designation || doctor.serviceName}
                                                        </p>
                                                    )}
                                                    <p className='text-sm text-[#0FA968] font-semibold mt-1'>
                                                        Fee: ₹{doctor.totalAmount}
                                                    </p>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.doctor && <p className="text-red-500 text-sm mt-1">{errors.doctor}</p>}
                                </div>
                            )}

                            {isDoctorSelected && (
                                <>
                                    {slotsLoading ? (
                                        <div className='mt-6 text-center'>
                                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA968]"></div>
                                            <p className="mt-2 text-gray-600">Loading available slots...</p>
                                        </div>
                                    ) : availableSlots.length > 0 ? (
                                        <>
                                            <div className='mt-6'>
                                                <p className='text-[#0FA968] text-base sm:text-[18px] font-roboto font-semibold'>AVAILABLE TIME SLOTS</p>
                                                <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-2'>
                                                    <span className='inline-flex items-center'><span className='py-1 px-4 bg-white rounded border border-gray-300'></span><span className="ms-2 text-sm sm:text-[16px]">Available</span></span>
                                                    <span className='inline-flex items-center'><span className='py-1 px-4 bg-[#D9D9DE] rounded'></span><span className="ms-2 text-sm sm:text-[16px]">Booked</span></span>
                                                    <span className='inline-flex items-center'><span className='py-1 px-4 bg-[#0FA968] rounded'></span><span className="ms-2 text-sm sm:text-[16px]">Selected</span></span>
                                                </div>
                                            </div>

                                            {/* Morning Slots */}
                                            {morningSlots.length > 0 && (
                                                <div className='mt-6'>
                                                    <h3 className='text-[#0FA968] text-[16px] font-semibold mb-3'>🌅 Morning (6:00 AM - 12:00 PM)</h3>
                                                    <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-4'>
                                                        {morningSlots.map((slot, index) => (
                                                            <label key={index} className='flex flex-col cursor-pointer'>
                                                                <input
                                                                    type='radio'
                                                                    name='slotTime'
                                                                    value={slot.slotTimeStr}
                                                                    checked={selectedSlot === slot.slotTimeStr}
                                                                    onChange={handleSlotChange}
                                                                    className='hidden'
                                                                />
                                                                <span
                                                                    className='text-center px-3 py-2 rounded text-[#27272A] text-[14px] bg-white hover:bg-[#DAFEEF] hover:text-[#0FA968] border border-gray-300'
                                                                    style={selectedSlot === slot.slotTimeStr ? { backgroundColor: '#0FA968', color: 'white', borderColor: '#0FA968' } : {}}
                                                                >
                                                                    {slot.slotTimeStr}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Afternoon Slots */}
                                            {afternoonSlots.length > 0 && (
                                                <div className='mt-6'>
                                                    <h3 className='text-[#0FA968] text-[16px] font-semibold mb-3'>☀️ Afternoon (12:00 PM - 5:00 PM)</h3>
                                                    <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-4'>
                                                        {afternoonSlots.map((slot, index) => (
                                                            <label key={index} className='flex flex-col cursor-pointer'>
                                                                <input
                                                                    type='radio'
                                                                    name='slotTime'
                                                                    value={slot.slotTimeStr}
                                                                    checked={selectedSlot === slot.slotTimeStr}
                                                                    onChange={handleSlotChange}
                                                                    className='hidden'
                                                                />
                                                                <span
                                                                    className='text-center px-3 py-2 rounded text-[#27272A] text-[14px] bg-white hover:bg-[#DAFEEF] hover:text-[#0FA968] border border-gray-300'
                                                                    style={selectedSlot === slot.slotTimeStr ? { backgroundColor: '#0FA968', color: 'white', borderColor: '#0FA968' } : {}}
                                                                >
                                                                    {slot.slotTimeStr}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Evening Slots */}
                                            {eveningSlots.length > 0 && (
                                                <div className='mt-6'>
                                                    <h3 className='text-[#0FA968] text-[16px] font-semibold mb-3'>🌙 Evening (5:00 PM onwards)</h3>
                                                    <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-4'>
                                                        {eveningSlots.map((slot, index) => (
                                                            <label key={index} className='flex flex-col cursor-pointer'>
                                                                <input
                                                                    type='radio'
                                                                    name='slotTime'
                                                                    value={slot.slotTimeStr}
                                                                    checked={selectedSlot === slot.slotTimeStr}
                                                                    onChange={handleSlotChange}
                                                                    className='hidden'
                                                                />
                                                                <span
                                                                    className='text-center px-3 py-2 rounded text-[#27272A] text-[14px] bg-white hover:bg-[#DAFEEF] hover:text-[#0FA968] border border-gray-300'
                                                                    style={selectedSlot === slot.slotTimeStr ? { backgroundColor: '#0FA968', color: 'white', borderColor: '#0FA968' } : {}}
                                                                >
                                                                    {slot.slotTimeStr}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {errors.slot && <p className="text-red-500 text-sm mt-2">{errors.slot}</p>}
                                        </>
                                    ) : (
                                        <div className='mt-6 text-center'>
                                            <p className="text-gray-600">No available slots for selected doctor today.</p>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className='flex flex-col-reverse sm:flex-row justify-center items-stretch sm:items-center mt-6 mb-6 gap-3 sm:gap-4'>
                                <Link
                                    to="/dashboard"
                                    type="button"
                                    className="px-4 py-2.5 bg-[#EFFEF7] text-[#0FA968] font-semibold rounded-lg shadow hover:bg-[#138F5E] hover:text-white transition duration-200 border border-[#0FA968] text-center"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200 text-base sm:text-[18px]"
                                >
                                    {selectedDoctor?.totalAmount && parseFloat(selectedDoctor.totalAmount) > 0 ? (
                                        <>Pay ₹{selectedDoctor.totalAmount} & Book Consultation</>
                                    ) : selectedDoctor?.totalAmount === 0 || selectedDoctor?.totalAmount === '0' ? (
                                        <>Book Free Consultation</>
                                    ) : (
                                        <>Book Consultation</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    )
}

export default BookConsultation;
